import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';

import { PreviousPageLocationConfiguration } from '@frontend/sports/common/client-config-data-access';
import { ExpiringCache } from '@frontend/sports/common/core/utils/expiring-cache';
import { Observable, firstValueFrom, of, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PreviousPageLocationCacheService {
    private previousPageCachetime: ExpiringCache;

    private injector = inject(Injector);

    constructor(private previousPageLocationConfiguration: PreviousPageLocationConfiguration) {
        this.initialisePreviousPageConfig();
    }

    private cachedNavigationId: number;
    private navigationId: number;
    private isBack: boolean;
    private readonly previousPageCacheKey = 'previousPage';

    private async initialisePreviousPageConfig(): Promise<void> {
        await firstValueFrom(this.previousPageLocationConfiguration.whenReady);
        runInInjectionContext(this.injector, () => {
            this.previousPageCachetime = new ExpiringCache(this.previousPageLocationConfiguration.caheTimeOut);
        });
    }

    getPreviousPage<T>(): Observable<T | undefined> {
        if (this.isBack && this.previousPageCachetime?.has(this.previousPageCacheKey)) {
            return this.previousPageCachetime.get<T>(this.previousPageCacheKey).pipe(
                tap(() => {
                    this.clearCache();
                }),
            );
        }

        return of(undefined);
    }

    setPreviousPage<T>(previousPage: T): void {
        if (!this.isBack) {
            this.cachedNavigationId = this.navigationId - 1; // First Routing Start is being called before destory of old component
            this.previousPageCachetime?.create(this.previousPageCacheKey, of(previousPage));
        }
    }

    setNavigationParameters(id: number, isBack: boolean): void {
        this.navigationId = id;
        this.isBack = isBack;
        if (this.cachedNavigationId && this.navigationId - this.cachedNavigationId > 1 && !this.isBack) {
            this.clearCache();
        }
    }

    private clearCache(): void {
        this.previousPageCachetime.clear();
    }
}
